import React from "react";
import { Candidate } from "../types";
import Tooltip from "./Tooltip";
import VerifiedBadge from "./VerifiedBadge";

interface CandidateCardProps {
  candidate: Candidate;
  onViewProfile: (candidate: Candidate) => void;
  onContact: (candidate: Candidate) => void;
  showActions?: boolean;
}

const CandidateCard: React.FC<CandidateCardProps> = ({
  candidate,
  onViewProfile,
  onContact,
  showActions = true,
}) => {
  const getTopSkills = () => {
    if (!candidate.skills?.length) return [];
    return candidate.skills.slice(0, 5);
  };

  const formatSalary = (salary?: number) => {
    if (!salary) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(salary);
  };

  const topSkills = getTopSkills();
  const isVerified =
    candidate.is_identity_verified === true || candidate.is_id_verified === true;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <span className="text-lg font-semibold text-indigo-600">
                {candidate.first_name?.[0]}
                {candidate.last_name?.[0]}
              </span>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {candidate.first_name} {candidate.last_name}
                </h3>
                {isVerified && <VerifiedBadge />}
              </div>
              {candidate.current_job_title && candidate.current_company && (
                <p className="text-sm text-gray-600">
                  {candidate.current_job_title} at {candidate.current_company}
                </p>
              )}
            </div>
          </div>

          {/* {candidate.ai_match_score && (
            <Tooltip
              content="How well this candidate matches your job requirements"
              position="left"
            >
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 cursor-help">
                {Math.round(candidate.ai_match_score * 100)}% Match
              </span>
            </Tooltip>
          )} */}
        </div>

        {/* Key Information */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 text-sm">
          {candidate.years_of_experience && (
            <div>
              <span className="text-gray-500">Experience</span>
              <p className="font-medium text-gray-900">
                {candidate.years_of_experience} years
              </p>
            </div>
          )}

          {candidate.location && (
            <div>
              <span className="text-gray-500">Location</span>
              <p className="font-medium text-gray-900">{candidate.location}</p>
            </div>
          )}

          {candidate.expected_salary && (
            <div>
              <span className="text-gray-500">Expected Salary</span>
              <p className="font-medium text-gray-900">
                {formatSalary(candidate.expected_salary)}
              </p>
            </div>
          )}
        </div>

        {/* Skills */}
        {topSkills.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Skills</h4>
            <div className="flex flex-wrap gap-2">
              {topSkills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {skill.skill_name}
                  {skill.proficiency_level && (
                    <span className="ml-1 text-blue-600">
                      ({skill.proficiency_level})
                    </span>
                  )}
                </span>
              ))}
              {candidate.skills && candidate.skills.length > 5 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  +{candidate.skills.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Comprehensive Data Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
          {/* Projects */}
          {candidate.projects && candidate.projects.length > 0 && (
            <div className="flex items-center space-x-1 text-purple-600">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                />
              </svg>
              <span>
                {candidate.projects.length} Project
                {candidate.projects.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Certifications */}
          {candidate.certifications && candidate.certifications.length > 0 && (
            <div className="flex items-center space-x-1 text-green-600">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              <span>
                {candidate.certifications.length} Cert
                {candidate.certifications.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Languages */}
          {candidate.languages && candidate.languages.length > 0 && (
            <div className="flex items-center space-x-1 text-blue-600">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                />
              </svg>
              <span>
                {candidate.languages.length} Language
                {candidate.languages.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          {/* Awards */}
          {candidate.awards && candidate.awards.length > 0 && (
            <div className="flex items-center space-x-1 text-yellow-600">
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
              <span>
                {candidate.awards.length} Award
                {candidate.awards.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {/* Top Projects Preview */}
        {candidate.projects && candidate.projects.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Recent Projects
            </h4>
            <div className="space-y-2">
              {candidate.projects.slice(0, 2).map((project, index) => (
                <div key={index} className="p-2 bg-gray-50 rounded border">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {typeof project === "string" ? project : project.name}
                      </p>
                      {typeof project === "object" && project.description && (
                        <p className="text-xs text-gray-600 line-clamp-1 mt-1">
                          {project.description}
                        </p>
                      )}
                      {typeof project === "object" &&
                        project.technologies &&
                        Array.isArray(project.technologies) &&
                        project.technologies.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {project.technologies
                              .slice(0, 3)
                              .map((tech, techIndex) => (
                                <span
                                  key={techIndex}
                                  className="inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700"
                                >
                                  {tech}
                                </span>
                              ))}
                            {project.technologies.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{project.technologies.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                    {typeof project === "object" && project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-purple-500 ml-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {candidate.projects.length > 2 && (
                <p className="text-xs text-gray-500 text-center">
                  +{candidate.projects.length - 2} more projects
                </p>
              )}
            </div>
          </div>
        )}

        {/* Top Certifications Preview */}
        {candidate.certifications && candidate.certifications.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Certifications
            </h4>
            <div className="flex flex-wrap gap-2">
              {candidate.certifications.slice(0, 3).map((cert, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                >
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  {typeof cert === "string" ? cert : cert.name}
                </span>
              ))}
              {candidate.certifications.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  +{candidate.certifications.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* AI Summary */}
        {candidate.ai_summary && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 line-clamp-2">
              {candidate.ai_summary}
            </p>
          </div>
        )}

        {/* Contact Information */}
        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
          {candidate.email && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              {candidate.email}
            </div>
          )}

          {candidate.phone && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              {candidate.phone}
            </div>
          )}
        </div>

        {/* Social Links */}
        {(candidate.linkedin_url ||
          candidate.github_url ||
          candidate.portfolio_url) && (
          <div className="flex items-center space-x-3 mb-4">
            {candidate.linkedin_url && (
              <a
                href={candidate.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            )}

            {candidate.github_url && (
              <a
                href={candidate.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </a>
            )}

            {candidate.portfolio_url && (
              <a
                href={candidate.portfolio_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-600 hover:text-purple-800"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9"
                  />
                </svg>
              </a>
            )}
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div className="flex space-x-3">
            <button
              onClick={() => onViewProfile(candidate)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              View Profile
            </button>
            {candidate.contact_unlocked && (
              <button
                onClick={() => onContact(candidate)}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Contact
              </button>
            )}
          </div>
        )}

        {/* Metadata */}
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
          <span>
            Added{" "}
            {candidate.created_at
              ? new Date(candidate.created_at).toLocaleDateString()
              : "Unknown"}
          </span>
          {candidate.ai_career_level && (
            <span>Level: {candidate.ai_career_level}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateCard;
